// FileRendererDataProvider.jsx
// Responsible for data storage and provision of the FileRenderer component.

// Copyright HS Analysis GmbH, 2023
// Author: Jakob Burkhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// HSA Imports
import { withPersistentStorage } from "../../viewer/contexts/PersistentStorageContext";
import Backend from "../../common/utils/Backend";
import FileViewerRenderer from "./FileViewerRenderer";

/**
 * Element containing all visualisations of all audio files / streams in a project.
 */
function FileRendererDataProvider(props) {
  const FV = props.FV;
  const [timeLineHeight, setTimeLineHeight] = React.useState(170);
  const [showZStackBar, setShowZStackBar] = React.useState(false);
  const [displayZStackBar, setDisplayZStackBar] = React.useState(false);
  const [displayTimeBar, setDisplayTimeBar] = React.useState(false);

  const showFullscreen = false;
  const zoomROI = false;
  const changingFile = false;

  //triggers on new Selected File
  React.useEffect(() => {
    if (!props.currentFileId) return;
    Backend.loadImage(
      { id: props.currentFileId },
      (ome) => {
        if (!ome.fileName) return;
        setShowZStackBar(ome.sizeZ > 1);
        setDisplayZStackBar(ome.sizeT > 1);
        setDisplayTimeBar(ome.sizeT > 1);
        FV.setImportProgress(-1);

        FV.setOmeDict((prevOmeDict) => ({
          ...prevOmeDict,
          [props.currentFileId]: ome,
        }));

        FV.setHistogramConfigs((prevConfigs) => ({
          ...prevConfigs,
          [props.currentFileId]: getHistogramFromOme(ome),
        }));
      },
      (error) => {
        console.log("error import", error);
      },
      (e) => {
        console.log("Import progress:", e);
        FV.setImportProgress(e.progress);
      }
    );

    return () => {
      FV.setOmeDict((prevOmeDict) => {
        const rest = { ...prevOmeDict };
        delete rest[props.currentFileId];
        return rest;
      });

      FV.setHistogramConfigs((prevConfigs) => {
        const rest = { ...prevConfigs };
        delete rest[props.currentFileId];
        return rest;
      });
    };
  }, [props.currentFileId]);

  const resizeTimeLine = (deltaY, resizeEnd) => {
    let newTimeLineHeight = timeLineHeight + deltaY;
    if (
      newTimeLineHeight > 90 &&
      window.innerHeight - newTimeLineHeight - 64 > 100
    ) {
      setTimeLineHeight(newTimeLineHeight);
      props.persistentStorage.save("timeLineHeight", newTimeLineHeight);
    }
    if (resizeEnd) {
      props.updateDimensions();
    }
  };

  const resetRoiZoomInDict = (fileId) => {
    let newZoomObjectDict = FV.zoomObjectDict;
    if (newZoomObjectDict) {
      newZoomObjectDict[fileId].zoomRoi = false;
      FV.setZoomObjectDict((prevState) => ({
        ...prevState,
        ...newZoomObjectDict,
      }));
    }
  };

  const getHistogramFromOme = (ome) => {
    let channels = [];
    for (let c of ome.channels) {
      let color = c.color.includes("#")
        ? c.color
        : "#" + parseInt(c.color).toString(16).padStart(6, "0");
      channels.push({
        color: color, //convert decimal color to hex color
        enabled: true,
        gamma: c.gamma,
        max: 250,
        min: 0,
        name: c.name,
        type: c.type,
      });
    }

    let histogramConfig = {
      channels: channels,
      nChannels: ome.channels.length,
    };

    return histogramConfig;
  };

  return (
    <div style={{ height: "100%" }}>
      {FV.histogramConfigs[props.currentFileId] &&
        FV.omeDict &&
        FV.omeDict[props.currentFileId] &&
        FV.activeFileId && (
          <FileViewerRenderer
            FV={FV}
            key={FV.containerKey}
            activeFileId={FV.activeFileId}
            canvasId={"caseViewerCanvas" + props.index}
            chainListFileIds={props.chainListFileIds}
            changingFile={changingFile}
            componentRef={(c) =>
              FV.setRendererDict({
                ...FV.rendererDict,
                [props.currentFileId]: c,
              })
            }
            displayTimeBar={displayTimeBar}
            displayZStackBar={displayZStackBar}
            selectedLayer={0}
            structures={[]}
            frameArrayDict={{}}
            resizeTimeLine={resizeTimeLine}
            timeLineHeight={timeLineHeight}
            fileId={props.currentFileId}
            histogramConfig={FV.histogramConfigs[props.currentFileId]}
            isActive={FV.activeFileId === props.currentFileId}
            isChained={FV.chainListFileIds.includes(props.currentFileId)}
            ome={FV.omeDict[props.currentFileId]}
            onChangeChain={() => {
              console.log("change chain");
            }}
            rendererDict={FV.rendererDict}
            rightSpace={50 + FV.previewWidth + FV.treeWidth}
            showFullscreen={showFullscreen}
            showZStackBar={showZStackBar}
            splitscreenCount={FV.splitscreenFileIds.length}
            splitscreenFileIds={FV.splitscreenFileIds}
            splitscreenIdx={props.index}
            tZoomROI1={() => {
              resetRoiZoomInDict();
            }}
            updateGlobalT={() => {
              return null;
            }}
            updateGlobalZ={() => {
              return null;
            }}
            zoomBottom={
              FV.zoomObjectDict[props.currentFileId]
                ? FV.zoomObjectDict[props.currentFileId].zoomBottom
                : 0
            }
            zoomLeft={
              FV.zoomObjectDict[props.currentFileId]
                ? FV.zoomObjectDict[props.currentFileId].zoomLeft
                : 0
            }
            zoomROI={zoomROI}
            zoomRight={
              FV.zoomObjectDict[props.currentFileId]
                ? FV.zoomObjectDict[props.currentFileId].zoomRight
                : 0
            }
            zoomTop={
              FV.zoomObjectDict[props.currentFileId]
                ? FV.zoomObjectDict[props.currentFileId].zoomTop
                : 0
            }
            getMousePosition={props.getMousePosition}
            openNextFile={props.openNextFile}
            openPrevFile={props.openPrevFile}
            updateDimensions={props.updateDimensions}
          />
        )}
    </div>
  );
}

FileRendererDataProvider.propTypes = {
  persistentStorage: PropTypes.object,
  updateDimensions: PropTypes.func,
  currentFileId: PropTypes.string,
  index: PropTypes.number,
  getMousePosition: PropTypes.func,
  openNextFile: PropTypes.func,
  openPrevFile: PropTypes.func,
  chainListFileIds: PropTypes.array,
  histogramConfigs: PropTypes.object, // Already present, but FV is the prop, not histogramConfigs directly
  FV: PropTypes.shape({
    histogramConfigs: PropTypes.object,
    omeDict: PropTypes.object,
    activeFileId: PropTypes.string,
    setImportProgress: PropTypes.func,
    setOmeDict: PropTypes.func,
    setHistogramConfigs: PropTypes.func,
    containerKey: PropTypes.any,
    rendererDict: PropTypes.object,
    setRendererDict: PropTypes.func,
    previewWidth: PropTypes.number,
    treeWidth: PropTypes.number,
    splitscreenFileIds: PropTypes.array,
    chainListFileIds: PropTypes.array,
    zoomObjectDict: PropTypes.object,
    setZoomObjectDict: PropTypes.func,
  }),
};

export default withPersistentStorage(FileRendererDataProvider);
